import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Heading,
  TextInputField,
  FormField,
  Button,
  Pane,
  IconButton,
  SelectField,
  CrossIcon,
  Switch,
} from 'evergreen-ui';
import { Container, Row, Col, setConfiguration } from 'react-grid-system';

import { mutate } from 'swr';
import { useForm } from 'react-hook-form';

import { useQueryBanks } from '../../services/api/banksService';
import {
  getEntry,
  putEntry,
  putEntryTransfer,
  useQueryEntries,
  useQueryEntry,
} from '../../services/api/entriesService';
import { getIntentColorByJournal, getNameByJournal } from '../../helpers/entriesHelper';
import styles from './EntryFormComponent.module.css';

import { toastNotification } from '../../shared/toastNotification';
import { inputTransferValidation } from '../../helpers/formHelper';
import { useSelector } from 'react-redux';
import { useQueryVitaUserByIdentityId } from '../../services/api/vitaUserService';
import { isTransferPairReconciled } from '../../helpers/reconciliationHelpers';

function TransferEditFormComponent({ isShown, setIsShown, setEntryJournalOnFocus }) {
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');
  const [fullEntryData, setFullEntryData] = useState(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isTransferReconciled, setIsTransferReconciled] = useState(false);
  const [originalValues, setOriginalValues] = useState(null);

  /* API calls */

  const { data: banks = [], isLoading: isLoadingBanks } = useQueryBanks(companyId, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const {
    data: vitaUser,
    isLoading: isLoadingVitaUser,
    isError,
  } = useQueryVitaUserByIdentityId(userId, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  // Get all entries for transfer pair reconciliation checking
  const { data: allEntries = [], isLoading: isLoadingEntries } = useQueryEntries(companyId, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  /* Form */

  const journalEntry = useSelector((state) => state.entryValues.transferEdit);

  const { formState, register, handleSubmit, reset, getValues, setValue, watch } = useForm({
    defaultValues: {
      ...journalEntry,
      statusPayment: journalEntry?.statusPayment || false,
      value:
        (fullEntryData?.value || journalEntry?.value)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
        }) || '0,00',
    },
  });

  setConfiguration({
    containerWidths: '100%',
    gutterWidth: '12',
  });

  const getTransferEntry = (journalEntry) => {
    if (!journalEntry || !banks?.data) return;

    console.log('getTransferEntryJournalEntry: ', journalEntry);
    const tempEntry = { ...journalEntry };
    tempEntry.dateCompetencia = tempEntry.dateCompetencia.substr(0, 10);
    tempEntry.dateVencimento = tempEntry.dateVencimento.substr(0, 10);

    // For transfers, we always want to show:
    // - bankAccountId: source account (Conta de Saída)
    // - bankAccountIdDestination: destination account (Conta de Entrada)
    // Regardless of which entry is being edited

    if (!tempEntry.isInput) {
      // Editing the source entry (saída) - journalEntryRefId is null
      // Source account is this entry's bankAccountId
      // Destination account is from the related entry
      tempEntry.bankAccountId = tempEntry.bankAccountId; // Source (saída)
      tempEntry.bankAccountIdDestination = tempEntry.journalEntryFromRef?.bankAccountId || null; // Destination (entrada)
    } else {
      // Editing the destination entry (entrada) - journalEntryRefId points to source
      // Source account is from the related entry
      // Destination account is this entry's bankAccountId
      const currentBankAccountId = tempEntry.bankAccountId; // Save current account (destination)
      tempEntry.bankAccountId = tempEntry.journalEntryRef?.bankAccountId || null; // Source (saída)
      tempEntry.bankAccountIdDestination = currentBankAccountId; // Destination (entrada) - use saved value
    }

    reset(tempEntry);
    setValue(
      'value',
      tempEntry.value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
      }),
      { shouldDirty: false } // Explicitly mark as not dirty when initializing
    );

    // Store original values for change detection
    setOriginalValues({
      bankAccountId: tempEntry.bankAccountId,
      bankAccountIdDestination: tempEntry.bankAccountIdDestination,
      dateCompetencia: tempEntry.dateCompetencia,
      description: tempEntry.description,
      value: tempEntry.value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
      }),
      statusPayment: tempEntry.statusPayment,
    });

    console.log('getTransferEntryUpdated: ', tempEntry);
  };

  // Load full entry data when dialog opens
  useEffect(() => {
    if (isShown && journalEntry?.id && !fullEntryData) {
      getEntry(journalEntry.id)
        .then((response) => {
          if (response.data) {
            setFullEntryData(response.data);
          }
        })
        .catch((error) => {
          console.error('Error loading full entry data:', error);
        });
    } else if (!isShown) {
      setFullEntryData(null);
      reset();
    }
  }, [isShown, journalEntry]);

  useEffect(() => {
    if (isShown && fullEntryData && banks?.data) {
      getTransferEntry(fullEntryData);
    }
  }, [isShown, fullEntryData, banks]);

  // Check if transfer is reconciled when data is loaded
  useEffect(() => {
    if (isShown && fullEntryData && allEntries?.data) {
      const transferReconciled = isTransferPairReconciled(fullEntryData, allEntries.data);
      setIsTransferReconciled(transferReconciled);
    }
  }, [isShown, fullEntryData, allEntries]);

  // Validate form fields for save button enable/disable
  useEffect(() => {
    inputTransferValidation(
      watch('bankAccountId'),
      watch('bankAccountIdDestination'),
      watch('dateCompetencia'),
      watch('value'),
      setIsDisabled
    );
  }, [
    watch('bankAccountId'),
    watch('bankAccountIdDestination'),
    watch('dateCompetencia'),
    watch('value'),
  ]);

  const handleInput = (event) => {
    let inputVal = event.target.value.replace(/\D/g, '');
    const initialValue = fullEntryData?.value || journalEntry?.value || 0;

    if (inputVal === '') {
      inputVal = '0';
    }

    // Convert both values to same format for comparison (cents)
    const newValue = Number(inputVal);
    const initialValueInCents = Math.round(initialValue * 100);

    // Format for display
    inputVal = inputVal.padStart(3, '0');
    const wholeNum = inputVal.slice(0, -2);
    const decimal = inputVal.slice(-2);
    const formattedWholeNum = Number(wholeNum).toLocaleString();
    const formattedValue = `${formattedWholeNum},${decimal}`;

    // Compare values in cents to avoid floating point issues
    setValue('value', formattedValue, {
      shouldDirty: newValue !== initialValueInCents,
    });
  };

  const buildFormHeader = () => {
    return (
      <Pane display="flex" flex={1} justifyContent="space-between" alignItems="center">
        <Button
          appearance="primary"
          intent={getIntentColorByJournal(journalEntry)}
          height={48}
          marginRight={16}
        >
          {getNameByJournal(journalEntry)}
        </Button>
        <Button
          appearance="minimal"
          height={28}
          marginRight={56}
          style={{
            background: '#BFFCC0',
            color: 'black',
          }}
          cursor="normal"
        >
          Modo Edição
        </Button>
        <IconButton appearance="minimal" icon={CrossIcon} onClick={onClose} />
      </Pane>
    );
  };

  // Check if form has changes compared to original values
  const hasFormChanges = () => {
    if (!originalValues) return false;

    const currentValues = getValues();

    return (
      currentValues.bankAccountId !== originalValues.bankAccountId ||
      currentValues.bankAccountIdDestination !== originalValues.bankAccountIdDestination ||
      currentValues.dateCompetencia !== originalValues.dateCompetencia ||
      currentValues.description !== originalValues.description ||
      currentValues.value !== originalValues.value ||
      currentValues.statusPayment !== originalValues.statusPayment
    );
  };

  const buildFormFooter = () => {
    const anyFieldChanged = hasFormChanges();
    console.log('hasFormChanges: ', anyFieldChanged);
    console.log('isDisabled: ', isDisabled);

    // For reconciled transfers, only enable save if description changed
    let isSaveDisabled = !anyFieldChanged || isDisabled;
    if (isTransferReconciled) {
      const currentValues = getValues();
      const descriptionChanged = currentValues.description !== originalValues?.description;
      isSaveDisabled = !descriptionChanged;
    }

    return (
      <Pane>
        <Button form="transfer-form" type="submit" width="fit-content" disabled={isSaveDisabled}>
          Salvar
        </Button>
      </Pane>
    );
  };

  const onSubmit = (entry) => {
    if (!journalEntry) return;

    let statusResponse = false;
    let toastContent = '';

    // Check if transfer is reconciled and only description was changed
    if (isTransferReconciled) {
      const currentValues = getValues();
      const hasOnlyDescriptionChanged =
        currentValues.description !== originalValues?.description &&
        currentValues.bankAccountId === originalValues?.bankAccountId &&
        currentValues.bankAccountIdDestination === originalValues?.bankAccountIdDestination &&
        currentValues.dateCompetencia === originalValues?.dateCompetencia &&
        currentValues.value === originalValues?.value &&
        currentValues.statusPayment === originalValues?.statusPayment;

      if (!hasOnlyDescriptionChanged) {
        toastNotification(
          'warning',
          'Para transferências conciliadas, apenas a descrição pode ser editada.'
        );
        return;
      }
    }

    // Start with all current form values
    const currentFormValues = getValues();

    console.log('currentFormValues: ', currentFormValues);

    // For destination entries (incoming transfers), the backend expects bank accounts in reverse order
    let bankAccountId = currentFormValues.bankAccountId;
    let bankAccountIdDestination = currentFormValues.bankAccountIdDestination;

    if (fullEntryData?.isInput === true) {
      // When editing destination entry (incoming transfer), swap the bank accounts
      // Backend expects: bankAccountId = destination, bankAccountIdDestination = source
      bankAccountId = currentFormValues.bankAccountIdDestination;
      bankAccountIdDestination = currentFormValues.bankAccountId;
    }

    const entryToUpdate = {
      ...currentFormValues,
      companyId,
      journalEntryType: 5,
      userUpdateId: !isLoadingVitaUser && vitaUser.data.id,
      dateCompetencia: currentFormValues.dateCompetencia,
      dateVencimento: currentFormValues.dateCompetencia,
      description: currentFormValues.description,
      value: Number(entry.value.replace(/\./g, '').replace(',', '.')),
      statusPayment: currentFormValues.statusPayment,
      bankAccountId: bankAccountId,
      bankAccountIdDestination: bankAccountIdDestination,
    };

    console.log('entryToUpdate: ', entryToUpdate);

    // Use the transfer-specific update method for transfer entries
    putEntryTransfer(entryToUpdate)
      .then((res) => {
        console.log('res put transfer entry: ', res);
        mutate('entriesFilteredByDate');
        mutate('entriesReconciliation');
        mutate((key) => typeof key === 'string' && key.startsWith('bankBalances-'));
        mutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-'));
        statusResponse = 'success';
        toastContent = 'Transferência salva com sucesso.';
        onClose();
      })
      .catch((err) => {
        statusResponse = 'warning';
        console.log('put transfer err', err);
        toastContent = `Verifique os campos novamente.`;
      })
      .finally(() => {
        toastNotification(statusResponse, toastContent);
        mutate('banks');
      });
  };

  const onClose = () => {
    reset();
    setIsShown(false);
  };

  const handleStatusPaymentChange = (e) => {
    setValue('statusPayment', e.target.checked);
  };

  return (
    <Dialog
      isShown={isShown}
      title=""
      onCloseComplete={onClose}
      confirmLabel="Custom Label"
      hasFooter={true}
      header={() => buildFormHeader()}
      footer={buildFormFooter}
      shouldCloseOnEscapePress={!hasFormChanges()}
      width={800}
      backgroundColor="#f8f8f8"
    >
      <div className={styles.dialogWrapper}>
        <form id="transfer-form" onSubmit={handleSubmit(onSubmit)}>
          <FormField className={styles.formArea}>
            <Container
              fluid={true}
              style={{
                width: '100%',
                margin: 'none',
                padding: 'none',
              }}
            >
              <Row>
                <Col sm={12} md={12} lg={6}>
                  <SelectField
                    {...register('bankAccountId')}
                    label="Conta de Saída"
                    value={watch('bankAccountId') || ''}
                    onChange={(e) => setValue('bankAccountId', e.target.value)}
                    disabled={isTransferReconciled}
                  >
                    <option value=""></option>
                    {!isLoadingBanks &&
                      banks.data.map((bank) => {
                        return (
                          <option key={bank.id} value={bank.id}>
                            {bank.name}
                          </option>
                        );
                      })}
                  </SelectField>
                </Col>
                <Col sm={12} md={12} lg={6}>
                  <SelectField
                    {...register('bankAccountIdDestination')}
                    label="Conta de Entrada"
                    value={watch('bankAccountIdDestination') || ''}
                    onChange={(e) => setValue('bankAccountIdDestination', e.target.value)}
                    disabled={isTransferReconciled}
                  >
                    <option value=""></option>

                    {!isLoadingBanks &&
                      banks.data.map((bank) => {
                        return (
                          <option key={bank.id} value={bank.id}>
                            {bank.name}
                          </option>
                        );
                      })}
                  </SelectField>
                </Col>
              </Row>
              <Row>
                <Col sm={12} md={6} lg={6}>
                  <TextInputField
                    {...register('dateCompetencia')}
                    label="Data da Transferência"
                    id="dateCompetencia"
                    name="dateCompetencia"
                    type="date"
                    disabled={isTransferReconciled}
                    onChange={(e) => {
                      setValue('dateCompetencia', e.target.value);
                    }}
                  />
                </Col>
                <Col sm={12} md={6} lg={6}>
                  <TextInputField
                    {...register('description')}
                    label="Descrição"
                    id="description"
                    name="description"
                    placeholder=""
                    defaultValue=""
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={10} sm={11} md={11} lg={12}>
                  <TextInputField
                    {...register('value')}
                    label="Valor (R$)"
                    type="text"
                    id="value"
                    name="value"
                    placeholder=""
                    disabled={isTransferReconciled}
                    defaultValue={
                      (fullEntryData?.value || journalEntry?.value)?.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      }) || '0,00'
                    }
                    onChange={handleInput}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={2} sm={1} md={1} lg={1}>
                  <Heading size={400} marginBottom={16}>
                    Pago
                  </Heading>
                  <Switch
                    {...register('statusPayment')}
                    checked={watch('statusPayment')}
                    disabled={isTransferReconciled}
                    onChange={handleStatusPaymentChange}
                  />
                </Col>
              </Row>
            </Container>
          </FormField>
        </form>
      </div>
    </Dialog>
  );
}

export default TransferEditFormComponent;
